/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import LoadingIndicator from 'components/LoadingIndicator';
import { default as ProductFeedComponent } from 'components/ProductFeed';
import logger from 'config/logger';
import { RequestInstance } from 'request';
import styles from './ProductFeed.module.scss';

const CTA_HREF_PREFIX = '/shop/all';
const CTA_HREF_TEXT = 'VIEW ALL';

async function getProductFeed({
  filters,
  limit,
  location,
  menu,
  skip = false,
  sort,
}) {
  try {
    if (skip) return [];
    const {
      result: { products: newProducts },
    } = await RequestInstance.post('api/menu/get-cached-menu', {
      json: {
        filters,
        limit,
        menu_type: menu,
        store_name: location,
        sort,
        use_composite_caches: false,
      },
    });
    return newProducts;
  } catch (err) {
    logger.error(
      err,
      `Error retrieving product feed for location:${location}, filters:${filters}`
    );
    return [];
  }
}

async function getProductFeeds({
  primaryFeed,
  limit,
  location,
  menu,
  secondaryFeed,
  targetSkus,
}) {
  const hasTargetSkus = targetSkus.length > 0;

  const { filters: primaryFilters, sort: primarySort } = primaryFeed;
  const {
    filters: secondaryFilters,
    header: secondaryHeader,
    sort: secondarySort,
    subheader: secondarySubheader,
  } = secondaryFeed;

  const useSecondaryFeed =
    !secondaryFilters &&
    !secondaryHeader &&
    !secondarySort &&
    !secondarySubheader;

  const [primaryFeedCall, secondaryFeedCall] = await Promise.allSettled([
    getProductFeed({
      filters: hasTargetSkus ? '' : primaryFilters,
      limit,
      location,
      menu,
      sort: hasTargetSkus ? '' : primarySort,
    }),
    getProductFeed({
      filters: secondaryFilters,
      limit,
      location,
      menu,
      skip: useSecondaryFeed,
      sort: secondarySort,
    }),
  ]);

  let primaryFeedProducts = [];
  let secondaryFeedProducts = [];

  if (primaryFeedCall.status === 'fulfilled')
    primaryFeedProducts = primaryFeedCall.value;

  if (secondaryFeedCall.status === 'fulfilled')
    secondaryFeedProducts = secondaryFeedCall.value;

  if (hasTargetSkus && primaryFeedProducts) {
    const targetSkusSet = new Set(targetSkus);
    const filteredFeedProducts = primaryFeedProducts.filter((product) =>
      targetSkusSet.has(product.SKU)
    );

    primaryFeedProducts = filteredFeedProducts;
  }

  return { primaryFeedProducts, secondaryFeedProducts };
}

function ProductFeed({
  feed_id: feedId,
  minimum_products_threshold: minimumProductsThreshold = 6,
  primary_feed: primaryFeed = {},
  productLabelsContent = {},
  product_limit: productLimit = 25,
  secondary_feed: secondaryFeed = {},
  target_skus: targetSkus = [],
}) {
  const storeSelection = useSelector((state) => state.storeSelection);
  const selectedMenu = useSelector((state) => state.selectedMenu);

  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [header, setHeader] = useState(null);
  const [subheader, setSubheader] = useState(null);
  const [ctaHref, setCtaHref] = useState(null);

  useEffect(() => {
    if (selectedMenu && storeSelection) {
      setIsLoading(true);
      getProductFeeds({
        primaryFeed,
        limit: productLimit,
        location: storeSelection,
        menu: selectedMenu,
        secondaryFeed,
        targetSkus,
      })
        .then((data) => {
          const { primaryFeedProducts: pfp, secondaryFeedProducts: sfp } = data;
          if (pfp?.length >= minimumProductsThreshold) {
            const {
              filters: pff,
              header: pfh,
              sort: pfs,
              subheader: pfsh,
            } = primaryFeed;
            setHeader(pfh);
            setSubheader(pfsh);
            setProducts(pfp?.slice(0, productLimit) ?? []);
            setCtaHref(
              `${CTA_HREF_PREFIX}?${pff ? `filters=${pff}` : ''}${
                pfs ? `&sort=${pfs}` : ''
              }`
            );
          } else {
            const {
              filters: sff,
              header: sfh,
              sort: sfs,
              subheader: sfsh,
            } = secondaryFeed;
            setHeader(sfh);
            setSubheader(sfsh);
            setProducts(sfp?.slice(0, productLimit) ?? []);
            setCtaHref(
              `${CTA_HREF_PREFIX}?${sff ? `filters=${sff}` : ''}${
                sfs ? `&sort=${sfs}` : ''
              }`
            );
          }
        })
        .catch(() => {
          setProducts([]);
        })
        .finally(() => setIsLoading(false));
    }
  }, [selectedMenu, storeSelection]);

  const { default_category_images: defaultCategoryImages } =
    productLabelsContent;

  return (
    <section className={classNames('mx-auto my-10 w-100', styles.wrapper)}>
      {products.length >= minimumProductsThreshold && !isLoading && (
        <ProductFeedComponent
          ctaHref={ctaHref}
          ctaHrefText={CTA_HREF_TEXT}
          defaultCategoryImages={defaultCategoryImages}
          feedId={feedId}
          header={header}
          products={products}
          subheader={subheader}
        />
      )}
      {isLoading && <LoadingIndicator />}
    </section>
  );
}

export default ProductFeed;
